import { Loader, Pill, Tooltip } from '@recruit-robin/robin-theme/web-components';
import { ChevronDown, ChevronUp, Edit_05 } from '@recruit-robin/robin-theme/web-icons';
import { useMemo } from 'react';

import type { JobSummaryViewModel } from '../../model';

import { JobQuality, getJobQuality } from '@/entities/job';
import { useDictionary } from '@/shared/lib/i18n';
import { useStyles } from './JobImprovedSelect.styles';

type JobImprovedSelectEndAdornmentProps = {
  isOpen: boolean;
  isJobListLoading: boolean;
  selectedJob: JobSummaryViewModel | null;
  onEditJob?: (job: JobSummaryViewModel) => void;
};

const pillTypeForLevel = (level: JobQuality): 'error' | 'warning' | 'success-light' => {
  switch (level) {
    case JobQuality.LOW:
      return 'error';
    case JobQuality.MEDIUM:
      return 'warning';
    default:
      return 'success-light';
  }
};

export const JobImprovedSelectEndAdornment = ({
  isOpen,
  isJobListLoading,
  selectedJob,
  onEditJob,
}: JobImprovedSelectEndAdornmentProps) => {
  const dictionary = useDictionary();
  const classes = useStyles();

  const qualityPill = useMemo(() => {
    const percent = selectedJob?.qualityPercent;
    if (percent == null) return null;

    const level = getJobQuality(percent);
    const label =
      level === JobQuality.LOW
        ? dictionary.lowReplyChance
        : level === JobQuality.MEDIUM
          ? dictionary.mediumReplyChance
          : dictionary.highReplyChance;

    return (
      <Pill
        label={label}
        style="icon-removable"
        type={pillTypeForLevel(level)}
        size="small"
        onClick={() => undefined}
        padding="text"
      />
    );
  }, [
    dictionary.highReplyChance,
    dictionary.lowReplyChance,
    dictionary.mediumReplyChance,
    selectedJob?.qualityPercent,
  ]);

  const editTooltipLabel = selectedJob?.isImproved ? dictionary.editVacancy : dictionary.enrichVacancy;

  const editButton =
    onEditJob && selectedJob ? (
      <Tooltip label={editTooltipLabel} zIndex={100} placement="bottom-end" offset={10}>
        <div
          css={classes.editIconButton}
          onClick={(event) => {
            event.stopPropagation();
            event.preventDefault();
            onEditJob(selectedJob);
          }}
          onMouseDown={(event) => {
            event.stopPropagation();
          }}>
          <Edit_05 size={16} color={(c) => c.neutral.variant[60]} />
        </div>
      </Tooltip>
    ) : null;

  return (
    <div css={classes.endIcon}>
      {qualityPill}
      {editButton}
      {isJobListLoading ? <Loader size={14} /> : isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
    </div>
  );
};
